<template>
  <div>
    <v-row class="justify-center onboarding-investment-amount-row">
      <v-col class="d-flex flex-column justify-center">
        <h1 class="mb-6" style="text-align: center">Dein Investitionsbetrag</h1>

        <h6 class="mb-1">Welchen Betrag möchtest du in deinem Portfolio anlegen?</h6>

        <fin-lexicon-text :text="`${investMoneyDescText}`" auto-solve/>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col v-for="(mc, index) in moneyCards" :key="index"
             cols="6"
             sm="3">
        <fin-money-card :amount="mc.amount"
                        :img-type="mc.name"
                        @click="selectionChanged"
                        :selected="selectedMoneyCard.name === mc.name"/>
      </v-col>
    </v-row>

    <v-row>
      <v-col align="center" cols="12" style="min-height: 148px">
        <v-form v-model="capitalForm" ref="capitalForm">
          <fin-currency-field v-if="selectedMoneyCard.name === 'custom'"
                              required
                              :rules="[ val => val>0 || 'Kapital muss positiv sein' ]"
                              v-model="capital"
                              style="max-width: 276px"
                              :nbOfDecimal="0"/>
          <v-btn :color="$finColors.primaryDark"
                 :dark="value !== -1"
                 :disabled="!capitalForm || value === -1"
                 class="font-weight-bold"
                 large
                 @click="confirmCapital">
            Auswahl bestätigen
          </v-btn>
        </v-form>
      </v-col>
    </v-row>

    <v-row class="pb-6">
      <v-col>
        <div style="text-align: center;">
          <fin-lexicon-text text="Du möchtest einen Sparplan anlegen?"/>
          <br/>
          <fin-lexicon-link to="sparplan">Hier</fin-lexicon-link>
          erfährst du die Tipps von finCraft zum Sparplanen.
        </div>

      </v-col>
    </v-row>

    <fin-dialog v-model="showNoticeDialog"
                :max-width="780"
                persistent
                @input="val => {if (val === false) {keepCapital()}}">
      <template #title>
        {{ noticeDialogTitle }}
      </template>
      <template>
        <v-card-text class="pa-0" style="line-height: 23px;">
          {{ noticeDialogText }}
        </v-card-text>
        <v-card-actions :class="'mt-3 d-flex justify-end '+($vuetify.breakpoint.xs ? 'mb-8' : '')">
          <div class="text-right d-flex">
            <v-btn class="mb-2"
                   outlined
                   @click="enterNewCapital">
              <span v-if="$vuetify.breakpoint.xsOnly">
                Ja, erhöhen
              </span>
              <span v-else>
                Ja, Betrag erhöhen
              </span>
            </v-btn>
            <v-btn class="ml-2 mb-2"
                   dark
                   @click="keepCapital">
               <span v-if="$vuetify.breakpoint.xsOnly">
                Nein, beibehalten
              </span>
              <span v-else>
                Nein, Betrag beibehalten
              </span>
            </v-btn>
          </div>
        </v-card-actions>
      </template>
    </fin-dialog>

  </div>
</template>

<script>
import FinMoneyCard from '@/components/pages/onboarding/Step3/MoneyCard/index'
import FinLexiconText from '@/components/common/lexicon/LexiconText/index'
import FinCurrencyField from '@/components/common/CurrencyField'
import FinLexiconLink from "@/components/common/lexicon/LexiconLink"

const MONEY_CARDS = [
  {
    name: 'small',
    amount: 5000
  },
  {
    name: 'medium',
    amount: 10000
  },
  {
    name: 'huge',
    amount: 50000
  },
  {
    name: 'custom',
    amount: 0
  }
]

export default {
  name: 'finInvestmentAmount',
  components: {FinLexiconLink, FinMoneyCard, FinLexiconText, FinCurrencyField},
  props: {
    /** the initial capital to select the correct money card, use -1 to select none */
    value: {
      type: Number,
      default: -1
    },
    /** Investing below this limit will reduce the assets to share etfs only */
    capitalLimit1: {
      type: Number
    },
    /** Investing below this limit will reduce the assets to share etfs + shares (split 50:50) only */
    capitalLimit2: {
      type: Number
    },
  },
  data() {
    return {
      capitalForm: true,
      selectedMoneyCard: {},
      investMoneyDescText: 'Entweder investierst du einen Einmalbetrag (Startkapital), der dir heute zur Verfügung steht, oder du ' +
        'legst die Zielgröße deines Portfolios (Zielkapital) fest, die du sukzessive ansparen möchtest.',
      capital: 0,
      moneyCards: MONEY_CARDS,
      showNoticeDialog: false,
      noticeDialogTitle: '',
      noticeDialogText: ''
    }
  },
  methods: {
    selectionChanged(val) {
      this.capital = val.amount
      this.selectedMoneyCard = val
      this.$nextTick( ()=> {
        this.$refs.capitalForm.resetValidation()
      })
    },
    markAsSelected() {
      if (this.value !== -1) {
        const initialMoneyCard = MONEY_CARDS.find(e => e.amount === this.value)

        if (initialMoneyCard !== undefined) {
          this.selectedMoneyCard = initialMoneyCard
        } else {
          this.$set(this.selectedMoneyCard, 'name', 'custom')
          this.$set(this.selectedMoneyCard, 'amount', this.value)
        }
      }
    },
    keepCapital() {
      this.showNoticeDialog = false
      this.$emit('confirm')
    },
    enterNewCapital() {
      this.showNoticeDialog = false
    },
    confirmCapital() {
      const valid = this.$refs.capitalForm.validate()
      if(valid){
        if (this.capital && this.capital < this.capitalLimit2) {
          this.showNoticeDialog = true
          this.noticeDialogTitle = 'Möchtest du den Betrag erhöhen?'
          this.noticeDialogText = this.capital < this.capitalLimit1 ?
              'Um für relativ kleine Beträge unter ' + this.capitalLimit2 + ' € eine ausreichende Diversifikation zu garantieren empfiehlt finCraft eine vereinfachte Strategie in Form von breit gestreuten Aktien ETFs. Die Assetallokation wird auf Aktien ETF reduziert.' :
              'Um für relativ kleine Beträge unter ' + this.capitalLimit2 + ' € eine ausreichende Diversifikation zu garantieren empfiehlt finCraft eine vereinfachte Assetallokation in Form von breit gestreuten Aktien ETFs und einem Korb von Aktien passend zu deinem Risikoprofil. Die Assetallokation wir entsprechend vereinfacht.'
        } else {
          this.keepCapital()
        }
      }
    },
  },
  watch: {
    model() {
      this.capital = -1
    }
  },
  beforeMount() {
    this.capital = this.value
  },
  mounted() {
    this.markAsSelected()
    // Register watch dynamically to avoid trigger on
    this.$watch('capital', (newVal) => {
      this.selectedMoneyCard.amount = newVal
      this.$emit("input", newVal)
    })
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../../styles/variables";

h6 {
  font-family: $body-font-family;
  font-size: 16px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: center;
  color: $fin-color-primary-dark;
}

</style>
